import { Constructor } from '@skylab-shared/types';
import { Query, QueryBus } from './../../query';
import { CacheStorage } from './../../storage';
import { Conditions, ContextBase, DataProvider } from './context-base';
import { RootContextBase } from './root-context';

export class LocalContext extends ContextBase {
    private readonly parent: ContextBase | null;
    private readonly children: ContextBase[];

    public readonly root: RootContextBase | null;

    public constructor(storage: CacheStorage,
        queryBus: QueryBus,
        root: RootContextBase | null,
        parent: ContextBase | null,
        name?: string) {
        super(storage, queryBus);

        this.root = root;
        this.parent = parent;
        this.children = [];
        this.name = name;
    }

    public async preload<T>(id: string): Promise<T>;
    public async preload<T>(id: string, dataProvider: DataProvider<T>): Promise<T>;
    public async preload<T>(query: Constructor<Query<T>>): Promise<T>;
    public async preload<T>(queryOrId: any, dataProvider?: any): Promise<T | T[]> {
        const data = await this.getAsync<T>(queryOrId, dataProvider);
        this.logger.info(`PRELOADED: ${typeof queryOrId === 'string' ? queryOrId : queryOrId.name}.`, this.name);

        return data;
    }

    public getPreloaded<T>(id: string): T;
    public getPreloaded<T>(query: Constructor<Query<T>>): T;
    public getPreloaded<T>(queryOrId: any): T {
        if (this.isDataProviderRegistered(queryOrId)) {
            const key = this.getStorageKey(queryOrId);

            if (!key) {
                throw new Error(`Data source ${key} is not registered.`);
            }

            if (this.getRepeatableDataProvidersIds().has(key) && !this.storage.hasKey(key)) {
                this.logger.warn(`${key} get preloaded failed, but request will be repeat next time.`);
            }

            return this.storage.get<T>(key);
        }

        const ctx = this.getContextWithData(queryOrId);
        if (ctx instanceof LocalContext) {
            return ctx.getPreloaded<T>(queryOrId);
        }

        throw new Error(`"${queryOrId.name || queryOrId}" not yet loaded.`);
    }

    public filterPreloaded<T>(query: Constructor<Query<T[]>>, conditions: Conditions<T>): T[] {
        const data = this.getPreloaded(query);
        if (!data) return [];
        const filter = this.createFilter(conditions);
        return this.applyFilter(data, filter);
    }

    public firstPreloaded<T>(query: Constructor<Query<T[]>>, conditions: Conditions<T>): T {
        return this.filterPreloaded(query, conditions).shift()!;
    }

    public override async getAsync<T>(id: string, dataProvider: DataProvider<T>): Promise<T>;
    public override async getAsync<T>(id: string): Promise<T>;
    public override async getAsync<T>(query: Constructor<Query<T>>): Promise<T>;
    public override async getAsync(queryOrId: any, dataProvider?: any): Promise<any> {
        try {
            const key = this.getStorageKey(queryOrId);
            if (this.isDataProviderRegistered(queryOrId)) {
                if (this.getRepeatableDataProvidersIds().has(key) && !this.storage.hasKey(key)) {
                    try {
                        await this.storage.refresh(key);
                        this.getRepeatableDataProvidersIds().delete(key);
                    } catch (err) {
                        this.logger.warn(`Repeated load ${key} gone wrong`);
                    }
                }
                return await super.getAsync(queryOrId);
            }

            const ctx = this.getContextWithData(queryOrId);
            if (ctx !== null) {
                return ctx.getAsync(queryOrId);
            }

            await this.registerAsync(queryOrId, dataProvider);

            return super.getAsync(queryOrId);
        } catch (err) {
            this.logger.error(err);
        }
    }

    public async firstAsync<T>(query: Constructor<Query<T[]>>, conditions: Conditions<T>): Promise<T> {
        const data = await this.getAsync(query);
        const filter = this.createFilter(conditions);
        return this.applyFilter(data, filter).shift()!;
    }

    public createLocalContext(name?: string): LocalContext {
        const ctx = new LocalContext(this.storage, this.queryBus, this.root, this, name);
        this.children.push(ctx);
        return ctx;
    }

    public override dispose(): void {
        if (this.children.length > 0)
            throw new Error(`You must dispose all children context first (children: ${this.children.length}).`);

        if (this.parent instanceof LocalContext) {
            const idx = this.parent.children.indexOf(this);
            this.parent.children.splice(idx, 1);
        }

        super.dispose();
    }

    private getContextWithData(id: string): ContextBase | null;
    private getContextWithData(dataSourceOrId: any): ContextBase | null {
        let ctx: ContextBase | null = this.parent;
        while (ctx !== null && !ctx.isDataProviderRegistered(dataSourceOrId)) {
            ctx = ctx instanceof LocalContext ? ctx.parent : null;
        }
        return ctx;
    }

    protected override getRepeatableDataProvidersIds(): Set<unknown> {
        return this.root?.getRepeatableDataProvidersIds() || super.getRepeatableDataProvidersIds();
    }
}
